/**
 * iOS-PWA-Install-Hinweis (2026-07-14).
 *
 * iOS Safari zeigt keinen automatischen „App installieren"-Prompt wie
 * Chrome/Edge. Ohne installierte PWA gibt es aber KEINE Push-Notifications
 * auf iPhone (Apple-Restriktion seit iOS 16.4).
 *
 * Diese Komponente zeigt ein dezentes Banner mit der Anleitung „Teilen →
 * Zum Home-Bildschirm" — aber nur wenn:
 *   - Wir sind auf iOS Safari
 *   - Die PWA ist NICHT bereits installiert (kein standalone-Mode)
 *   - Der User hat den Hinweis nicht schon weggeklickt (localStorage)
 *
 * Wird in PortalShell direkt unter der Topbar gerendert.
 */
import { useEffect, useState } from 'react';
import { Share, X } from 'lucide-react';

const STORAGE_KEY = 'ios-install-hint-dismissed:v1';

function isIosSafariAndNotInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream: unknown }).MSStream;
  if (!isIos) return false;
  // Nur Safari (kein Chrome-on-iOS / kein Facebook-In-App-Browser — die
  // koennen nichts installieren und der Hinweis waere irrefuehrend).
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPT|OPiOS/.test(ua);
  if (!isSafari) return false;
  // Bereits installiert = standalone-Mode
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return !standalone;
}

export default function IosInstallHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === '1') return;
    if (!isIosSafariAndNotInstalled()) return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  return (
    <div className="bg-brand-600/10 border-b border-brand-600/30 px-3 py-2 flex items-start gap-2 text-xs text-brand-200">
      <Share className="h-4 w-4 shrink-0 mt-0.5 text-brand-300" aria-hidden="true" />
      <div className="flex-1 leading-snug">
        <strong>Portal aufs iPhone installieren:</strong>{' '}
        Tippe unten in Safari auf <span className="whitespace-nowrap">„Teilen"</span> und waehle{' '}
        <span className="whitespace-nowrap">„Zum Home-Bildschirm"</span>.
        Erst dann kommen Push-Nachrichten auf dein Handy.
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 p-1 rounded hover:bg-brand-600/20 text-brand-200"
        aria-label="Hinweis schliessen"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
